import React, { useEffect, useRef } from 'react';
import { LAYERS } from './layers';

/**
 * § 242 — anchored surfaces follow their anchor.
 *
 * Every anchored popover in the system measured its trigger ONCE, at open:
 * `Dropdown`'s portalled listbox took a fixed `top/left` from one
 * `getBoundingClientRect()` and never looked again, `Tooltip`'s portalled bubble the
 * same, and the inline popovers (`Combobox`, `DropdownButton`) never measured at all,
 * so none could open upward. Scroll the page (or a drawer) with a listbox open and
 * the list stayed where the trigger USED to be — the defect every positioning engine
 * exists to close, and the one `@floating-ui`'s `autoUpdate` is for.
 *
 * This is that half of an engine, and deliberately no more: a subscription that
 * re-runs `update` while `active`, on the three things that move an anchor —
 *
 * - **scroll, captured on `window`**: capture phase, so a scroll inside ANY ancestor
 *   scroller (the app's `<main>`, a SidePanel, a Dialog body) reaches it — `scroll`
 *   does not bubble, so a bubbling listener would only ever hear the document;
 * - **resize** of the window;
 * - **the anchor's own size** (ResizeObserver — a trigger that grows when its label
 *   changes moves its bottom edge without any scroll or resize).
 *
 * Not a dependency: `@floating-ui/react` is in `node_modules` only as
 * react-datepicker's transitive dependency, and the design system may not import
 * what it does not declare (`dependencyDirection.test.ts` polices the direction; a
 * hoisting change would police the rest). The whole engine is ~30 lines here, testable
 * in jsdom by dispatching one event.
 *
 * `update` is read through a ref so callers can pass an inline closure without the
 * subscription tearing down and re-attaching on every render.
 */
export function useAnchorUpdates(
  active: boolean,
  anchor: React.RefObject<Element | null>,
  update: () => void,
  /** § 334 — also re-measure when the SURFACE resizes (content landing, a filter narrowing). */
  floating?: React.RefObject<Element | null>,
): void {
  const updateRef = useRef(update);
  updateRef.current = update;

  useEffect(() => {
    if (!active) return undefined;
    const run = () => updateRef.current();
    window.addEventListener('scroll', run, true);
    window.addEventListener('resize', run);
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined' && anchor.current) {
      observer = new ResizeObserver(run);
      observer.observe(anchor.current);
      if (floating?.current) observer.observe(floating.current);
    }
    return () => {
      window.removeEventListener('scroll', run, true);
      window.removeEventListener('resize', run);
      observer?.disconnect();
    };
  }, [active, anchor, floating]);
}

/**
 * The room a popover needs below its anchor before it flips: the listbox's
 * `max-h-64` (256px) plus the 4px gap. One number, shared by every popover that
 * flips, so they all flip at the same scroll position.
 */
export const POPOVER_ROOM_PX = 260;

/**
 * Should the surface open ABOVE the anchor? Only when there is not enough room
 * below AND more room above — the old `spaceBelow < 260` alone flipped a trigger
 * near the TOP of a short viewport upward into even less space.
 */
export const preferUpward = (rect: DOMRect, neededPx: number): boolean => {
  const below = window.innerHeight - rect.bottom;
  return below < neededPx && rect.top > below;
};

/** Which horizontal edge of the anchor the surface hangs from. */
export type AnchorAlign = 'start' | 'end';

export interface FixedAnchorOptions {
  /** Hang from the anchor's top edge (the § 242 flip) instead of its bottom. */
  above: boolean;
  align?: AnchorAlign;
  /** Size the surface to the anchor's width — a listbox under its field. */
  matchWidth?: boolean;
  /** The 4px the inline surfaces always kept between themselves and the anchor. */
  gapPx?: number;
  /**
   * § 334 — publish the room actually available on the side the surface opens, as the
   * `--ds-popover-room` custom property. A surface caps itself with
   * `min(<its own max>, var(--ds-popover-room))`, so with room to spare nothing changes and
   * in a cramped viewport it scrolls instead of running off the screen.
   */
  fit?: boolean;
  /**
   * § 334 — the surface's rendered width, once it has one. With it, a surface that would
   * cross the viewport edge slides back inside `VIEWPORT_PAD_PX`, keeping the side it hangs
   * from (`left` stays `left`, `right` stays `right`). Without it, placement is as before.
   */
  floatingWidth?: number;
  /**
   * § 334 — the surface's NATURAL content height (its `scrollHeight`, so a cap already
   * applied cannot feed back into the measure). With `fit`, a surface taller than the room
   * on its side is capped at that room and scrolls; one that fits is left exactly as it was.
   */
  floatingHeight?: number;
  /**
   * § 334 — the anchor element. When it has been scrolled out of a clipping ancestor (or
   * the viewport), the surface is hidden rather than left floating over unrelated content.
   */
  anchorEl?: Element | null;
}

/** The margin a surface keeps from the viewport edge when it has to be moved. */
export const VIEWPORT_PAD_PX = 8;

/**
 * Is the anchor outside what its scroll containers — or the viewport — still show?
 * Ancestors that have not been laid out (a zero box, as in jsdom) are ignored, so an
 * unmeasurable tree never hides anything.
 */
export const anchorIsClipped = (el: Element, rect: DOMRect): boolean => {
  const outside = (r: { top: number; bottom: number; left: number; right: number }) =>
    rect.bottom <= r.top || rect.top >= r.bottom || rect.right <= r.left || rect.left >= r.right;
  if (rect.width === 0 && rect.height === 0) return false;
  if (outside({ top: 0, left: 0, bottom: window.innerHeight, right: window.innerWidth })) return true;
  for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
    const style = window.getComputedStyle(node);
    if (!/(auto|scroll|hidden|clip)/.test(`${style.overflow}${style.overflowX}${style.overflowY}`)) continue;
    const box = node.getBoundingClientRect();
    if (box.width === 0 && box.height === 0) continue;
    if (outside(box)) return true;
  }
  return false;
};

/**
 * § 304 — the fixed placement of a PORTALLED surface against its anchor's rect.
 *
 * Dropdown carried this arithmetic alone (`calculatePosition`) while Combobox,
 * Popover and DropdownButton rendered inline and clipped inside any scrolling
 * ancestor. It is the other half of the engine above: `useAnchorUpdates` says WHEN
 * to re-measure, this says WHERE the surface goes. `bottom` / `right` rather than a
 * computed `top` / `left` when hanging above or from the end, so the surface's own
 * size never enters the arithmetic. The z is the `popover` rung.
 */
export const fixedAnchorStyle = (
  rect: DOMRect,
  {
    above, align = 'start', matchWidth = false, gapPx = 4, fit = false, floatingWidth, floatingHeight, anchorEl,
  }: FixedAnchorOptions,
): React.CSSProperties => {
  const vw = window.innerWidth;
  const pad = VIEWPORT_PAD_PX;
  let horizontal: React.CSSProperties = align === 'end' ? { right: vw - rect.right } : { left: rect.left };
  // § 334 — shift, only once the surface's width is known and only if it would cross an edge.
  if (floatingWidth && floatingWidth > 0) {
    if (align === 'end') {
      const leftEdge = rect.right - floatingWidth;
      if (leftEdge < pad) horizontal = { right: Math.max(pad, vw - pad - floatingWidth) };
      else if (rect.right > vw - pad) horizontal = { right: pad };
    } else if (rect.left + floatingWidth > vw - pad) {
      horizontal = { left: Math.max(pad, vw - pad - floatingWidth) };
    } else if (rect.left < pad) {
      horizontal = { left: pad };
    }
  }
  const style: React.CSSProperties = {
    position: 'fixed',
    ...(above ? { bottom: window.innerHeight - rect.top + gapPx } : { top: rect.bottom + gapPx }),
    ...horizontal,
    ...(matchWidth ? { width: rect.width } : {}),
    zIndex: LAYERS.popover,
  };
  if (fit) {
    const room = above ? rect.top - gapPx - pad : window.innerHeight - rect.bottom - gapPx - pad;
    const roomPx = Math.max(0, Math.floor(room));
    (style as Record<string, string | number>)['--ds-popover-room'] = `${roomPx}px`;
    if (floatingHeight && floatingHeight > roomPx) {
      style.maxHeight = roomPx;
      style.overflowY = 'auto';
    }
  }
  if (anchorEl && anchorIsClipped(anchorEl, rect)) {
    style.visibility = 'hidden';
    style.pointerEvents = 'none';
  }
  return style;
};

/** Same fixed position → same object, so a scroll that moved nothing re-renders nothing. */
export const samePosition = (a: React.CSSProperties, b: React.CSSProperties): boolean => {
  const room = (s: React.CSSProperties) => (s as Record<string, unknown>)['--ds-popover-room'];
  return a.top === b.top && a.bottom === b.bottom && a.left === b.left && a.right === b.right
    && a.width === b.width && a.visibility === b.visibility && room(a) === room(b) && a.maxHeight === b.maxHeight;
};
